import type { OffersT } from '../offers/offersI18n';

/**
 * #995: the garages 422 `PRICE_BELOW_OFFER_DISCOUNT` — a service price a running (or pending) offer
 * covering the service could not discount. `details` names the offer and the lowest price it still
 * discounts (FIXED: amount + 1).
 */
export const PRICE_GUARD_CODE = 'PRICE_BELOW_OFFER_DISCOUNT';

export interface PriceGuard {
  offerId: string;
  offerTitle: string;
  offerTitleAr: string | null;
  discountType: 'PERCENT' | 'FIXED';
  discountValue: number;
  minimumPrice: number;
  price: number;
  /** `price` (variant / V1 price) or `flatPrice` (single price). */
  field: string;
}

/** The guard details of an error, or null when it is any other error. */
export function priceGuardOf(err: unknown): PriceGuard | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { errorCode?: string | null; details?: Record<string, unknown> | null };
  if (e.errorCode !== PRICE_GUARD_CODE || !e.details) return null;
  const d = e.details;
  const minimumPrice = Number(d.minimumPrice);
  if (!Number.isFinite(minimumPrice)) return null;
  return {
    offerId: String(d.offerId ?? ''),
    offerTitle: String(d.offerTitle ?? ''),
    offerTitleAr: typeof d.offerTitleAr === 'string' ? d.offerTitleAr : null,
    discountType: d.discountType === 'PERCENT' ? 'PERCENT' : 'FIXED',
    discountValue: Number(d.discountValue),
    minimumPrice,
    price: Number(d.price),
    field: typeof d.field === 'string' ? d.field : 'price',
  };
}

const amount = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** "This price is covered by the offer '…' (AED 30 off). Minimum price while the offer runs: AED 31. …" */
export function priceGuardMessage(guard: PriceGuard, t: OffersT, lang: 'en' | 'ar'): string {
  const title = lang === 'ar' && guard.offerTitleAr ? guard.offerTitleAr : guard.offerTitle;
  const off = guard.discountType === 'PERCENT'
    ? t('offers.priceGuard.offPercent', { pct: amount(guard.discountValue) })
    : t('offers.priceGuard.offFixed', { amount: amount(guard.discountValue) });
  return t('offers.priceGuard.message', { title, off, min: amount(guard.minimumPrice) });
}

/** True when a typed price is under the guard's minimum (an empty field is not). */
export function belowGuard(value: string, guard: PriceGuard | null): boolean {
  if (!guard || value.trim() === '') return false;
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 && n < guard.minimumPrice;
}
